import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, RefreshCw, Send, Sparkles } from 'lucide-react';
import { Button } from 'src/components/ui/button/button';
import {
  Sheet,
  SheetCloseButton,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import { Textarea } from 'src/components/ui/textarea';
import { cn } from 'src/lib/utils';
import { useDraftDescription } from 'src/features/ai-assist/use-draft-description';
import { DraftDescriptionSurface } from 'src/lib/services/api/ai-agent-api';

interface HelpMeWritePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  surface: DraftDescriptionSurface;
  /** Forwarded as `existing_text` so the AI sees what the user has so far. */
  currentText?: string;
  videoType?: string | null;
  brandName?: string | null;
  /** Called when the user clicks Insert. Consumer writes the value; panel then closes. */
  onInsert: (draft: string) => void;
  /** Override the panel title — defaults to "Help me write". */
  title?: string;
}

/**
 * Side-panel "Help me write" UI inspired by Google Docs. Auto-drafts on open
 * when there's enough context (videoType or existing text); otherwise waits
 * for an instruction so the first call isn't a generic token burn. Refinement
 * is single-pass against the latest draft — no chat history, cheap and
 * predictable.
 */
const HelpMeWritePanel: React.FC<HelpMeWritePanelProps> = ({
  open,
  onOpenChange,
  surface,
  currentText,
  videoType,
  brandName,
  onInsert,
  title,
}) => {
  const { t } = useTranslation();
  const draftMutation = useDraftDescription();
  // Local state for draft + error so we can session-gate writes (a previous
  // session's response/error must not clobber the freshly opened panel) and
  // keep the previous draft visible during Regenerate (hidden by the
  // `!isLoading` JSX guard, not by clearing this state mid-request).
  const [latestDraft, setLatestDraft] = React.useState<string | null>(null);
  const [error, setError] = React.useState<unknown>(null);
  const [instruction, setInstruction] = React.useState('');
  // Auto-draft-on-first-open guard — without it, every re-open burns an LLM call.
  const autoDraftedRef = React.useRef(false);
  // Bumps on every open. mutate() captures the current id; onSuccess/onError
  // no-op when the captured id is stale. `draftMutation.reset()` clears
  // state but does NOT cancel the in-flight request, so the race is real.
  const sessionIdRef = React.useRef(0);

  // Reset panel state on CLOSE so the next open's first render is already
  // clean (avoids a one-frame flash of the previous session's draft/error).
  // On open we only bump session + clear the auto-draft flag — in-flight
  // requests still resolve, but the session guard makes them no-op writes.
  React.useEffect(() => {
    if (open) {
      sessionIdRef.current += 1;
      autoDraftedRef.current = false;
      draftMutation.reset();
      return;
    }
    setLatestDraft(null);
    setError(null);
    setInstruction('');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- draftMutation is stable
  }, [open]);

  const hasEnoughInitialContext = !!videoType || !!currentText?.trim();

  const runDraft = React.useCallback(() => {
    const session = sessionIdRef.current;
    draftMutation.mutate(
      {
        surface,
        mode: 'draft',
        video_type: videoType ?? null,
        brand_name: brandName ?? null,
        existing_text: currentText?.trim() ? currentText : null,
      },
      {
        onSuccess: (data) => {
          if (session !== sessionIdRef.current) return;
          setLatestDraft(data.draft);
          setError(null);
        },
        onError: (err) => {
          if (session !== sessionIdRef.current) return;
          setError(err);
        },
      },
    );
  }, [draftMutation, surface, videoType, brandName, currentText]);

  const runRefine = React.useCallback(() => {
    const trimmed = instruction.trim();
    if (!trimmed) return;
    const session = sessionIdRef.current;
    // First-send with no draft + empty consumer field: `existing_text` is
    // null and we still send mode='refine'. The wire shape reads odd
    // ("refine what?") but the backend prompt builder falls through to
    // "Write a brief that follows this instruction: …", which is correct.
    // Routing this case through mode='draft' would ask the LLM to *tighten*
    // the instruction text instead of write from it — worse output. The
    // right longer-term fix is for the backend to accept `instruction` in
    // draft mode as a first-prompt seed (separate PR).
    draftMutation.mutate(
      {
        surface,
        mode: 'refine',
        video_type: videoType ?? null,
        brand_name: brandName ?? null,
        // Symmetric with runDraft's null-coalesce: '' → null on the wire.
        existing_text: latestDraft ?? (currentText?.trim() ? currentText : null),
        instruction: trimmed,
      },
      {
        onSuccess: (data) => {
          if (session !== sessionIdRef.current) return;
          setLatestDraft(data.draft);
          setInstruction('');
          setError(null);
        },
        onError: (err) => {
          if (session !== sessionIdRef.current) return;
          setError(err);
        },
      },
    );
  }, [draftMutation, surface, videoType, brandName, latestDraft, currentText, instruction]);

  // Auto-draft on first open when there's enough context. Without context, a
  // blind first call would burn tokens on a generic placeholder.
  React.useEffect(() => {
    if (
      open
      && !autoDraftedRef.current
      && hasEnoughInitialContext
      && !latestDraft
      && !draftMutation.isLoading
    ) {
      autoDraftedRef.current = true;
      runDraft();
    }
  }, [open, hasEnoughInitialContext, latestDraft, draftMutation.isLoading, runDraft]);

  const errorMessage = mutationErrorMessage(error, t);

  const handleInsert = () => {
    if (!latestDraft) return;
    onInsert(latestDraft);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={'right'}
        // Lift above any open Dialog. Sheet defaults are z-998 (overlay) /
        // z-999 (content); Dialog defaults are z-1000 / z-1000. Create
        // Request renders this panel from inside an open Dialog (see
        // RequestWizard) — without the bump, the Sheet portals to body
        // *under* the Dialog backdrop and the user clicks "Help me write"
        // to no visible effect. Surface 2 (Revisions chat) opens this
        // panel outside any Dialog, where the bump is a harmless no-op.
        className={'tw-z-[1002] tw-flex tw-w-full tw-flex-col tw-gap-0 sm:tw-max-w-md'}
        overlayClassName={'tw-z-[1001]'}
      >
        <SheetHeader className={'tw-flex-row tw-items-center tw-justify-between tw-space-y-0'}>
          <SheetTitle className={'tw-flex tw-items-center tw-gap-2'}>
            <Sparkles className={'tw-size-4 tw-text-primary'} />
            {title ?? t('ai_assist.title', { defaultValue: 'Help me write' })}
          </SheetTitle>
          <SheetCloseButton />
        </SheetHeader>

        <div className={'tw-flex tw-flex-1 tw-flex-col tw-gap-4 tw-overflow-y-auto tw-p-4'}>
          {/* Initial empty state — show before the first draft has come back. */}
          {!latestDraft && !draftMutation.isLoading && (
            <div className={'tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-8 tw-text-center'}>
              <Sparkles className={'tw-size-8 tw-text-muted-foreground'} />
              <p className={'tw-text-sm tw-text-muted-foreground'}>
                {hasEnoughInitialContext
                  ? t('ai_assist.idle_with_context', {
                      defaultValue: 'Click below to draft from your selected video type.',
                    })
                  : t('ai_assist.idle_no_context', {
                      defaultValue:
                        'Tell me what you want to write about and I’ll draft something.',
                    })}
              </p>
              {hasEnoughInitialContext && (
                <Button
                  type={'button'}
                  variant={'default'}
                  size={'sm'}
                  onClick={runDraft}
                  className={'tw-gap-2'}
                >
                  <Sparkles className={'tw-size-4'} />
                  {t('ai_assist.draft_button', { defaultValue: 'Draft for me' })}
                </Button>
              )}
            </div>
          )}

          {/* Loading state — shown both for the first draft and for refinements. */}
          {draftMutation.isLoading && (
            <div className={'tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-8'}>
              <Loader2 className={'tw-size-6 tw-animate-spin tw-text-primary'} />
              <p className={'tw-text-sm tw-text-muted-foreground'}>
                {t('ai_assist.writing', { defaultValue: 'Writing…' })}
              </p>
            </div>
          )}

          {/* Latest draft card. Replaced in place on each refinement. */}
          {latestDraft && !draftMutation.isLoading && (
            <div
              className={
                'tw-flex tw-flex-col tw-gap-3 tw-rounded-md tw-border tw-border-border tw-bg-muted/40 tw-p-3'
              }
            >
              <p className={'tw-whitespace-pre-wrap tw-text-sm tw-leading-relaxed'}>
                {latestDraft}
              </p>
              <div className={'tw-flex tw-flex-wrap tw-justify-end tw-gap-2'}>
                <Button
                  type={'button'}
                  variant={'outline'}
                  size={'sm'}
                  onClick={runDraft}
                  disabled={draftMutation.isLoading}
                  className={'tw-gap-2'}
                >
                  <RefreshCw className={'tw-size-3.5'} />
                  {t('ai_assist.regenerate', { defaultValue: 'Regenerate' })}
                </Button>
                <Button
                  type={'button'}
                  variant={'default'}
                  size={'sm'}
                  onClick={handleInsert}
                  disabled={draftMutation.isLoading}
                >
                  {t('ai_assist.insert', { defaultValue: 'Insert' })}
                </Button>
              </div>
            </div>
          )}

          {errorMessage && (
            <p className={'tw-text-sm tw-text-destructive'}>{errorMessage}</p>
          )}
        </div>

        {/* Instruction input — pinned at bottom so refinements don't require scrolling. */}
        <div className={'tw-border-t tw-border-border tw-p-3'}>
          <div className={'tw-flex tw-items-end tw-gap-2'}>
            <Textarea
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              onKeyDown={(e) => {
                // Cmd/Ctrl+Enter to send; plain Enter inserts a newline.
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  runRefine();
                }
              }}
              placeholder={
                latestDraft
                  ? t('ai_assist.refine_placeholder', {
                      defaultValue: 'e.g. make it shorter, more formal, add a CTA…',
                    })!
                  : t('ai_assist.first_prompt_placeholder', {
                      defaultValue: 'Tell me what to write…',
                    })!
              }
              className={cn('tw-min-h-[60px] tw-resize-none')}
              disabled={draftMutation.isLoading}
            />
            <Button
              type={'button'}
              variant={'default'}
              size={'icon'}
              onClick={runRefine}
              disabled={draftMutation.isLoading || !instruction.trim()}
              aria-label={t('ai_assist.send_aria', { defaultValue: 'Send' })!}
            >
              {draftMutation.isLoading ? (
                <Loader2 className={'tw-size-4 tw-animate-spin'} />
              ) : (
                <Send className={'tw-size-4'} />
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

/**
 * Translates a session-current mutation error into a human-readable string.
 * 429 / 422 get specific copy; everything else falls through to generic.
 * The shared Http interceptor wraps axios errors in HttpError, which exposes
 * the response status as `code` — `instanceof AxiosError` would be dead code.
 *
 * On 422, prefer the upstream-forwarded reason that be-core puts in
 * `errors.message[0]` (e.g. "Rate limit exceeded", "`instruction` is required
 * when mode is `refine`", a content-policy rejection) over the generic
 * fallback. Karina's review on #2290 flagged the original copy as opaque
 * because every distinct upstream failure collapsed to the same string —
 * surfacing the real reason lets the user act on it and unblocks debugging.
 */
function mutationErrorMessage(
  error: unknown,
  t: (key: string, opts?: any) => string,
): string | null {
  if (!error) return null;
  const code = (error as { code?: number | string })?.code;
  if (code === 429) {
    return t('ai_assist.error_rate_limit', {
      defaultValue: 'You’re drafting fast — give it a minute and try again.',
    });
  }
  if (code === 422) {
    const upstreamMessage = extractUpstreamMessage(error);
    if (upstreamMessage) return upstreamMessage;
    return t('ai_assist.error_validation', {
      defaultValue:
        'Something about that request was off. Try a shorter instruction.',
    });
  }
  return t('ai_assist.error_generic', {
    defaultValue: 'Something went wrong. Please try again.',
  });
}

/**
 * Pulls the upstream reason out of be-core's 422 envelope. Laravel renders
 * `ValidationException::withMessages(['message' => $x])` as
 * `{ message: "The given data was invalid.", errors: { message: [$x] } }`,
 * so the useful string lives at `errors.message[0]`. The shared `HttpError`
 * assigns `errors = data.errors` directly (the typed `.form` wrapper is
 * aspirational, not what's actually on the wire), so cope with both shapes.
 * The Laravel-generic top-level `message` is suppressed because surfacing
 * "The given data was invalid." to a customer is worse than the fallback copy.
 */
function extractUpstreamMessage(error: unknown): string | null {
  const errors = (error as { errors?: unknown })?.errors;
  if (!errors || typeof errors !== 'object') return null;
  const candidates = [
    (errors as { message?: unknown }).message,
    (errors as { form?: { message?: unknown } }).form?.message,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate;
    if (
      Array.isArray(candidate)
      && typeof candidate[0] === 'string'
      && candidate[0].trim()
    ) {
      return candidate[0];
    }
  }
  return null;
}

export { HelpMeWritePanel };
